import { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import CTypography from "../../componets/typography";
import { CBaseButton } from "../../componets/button/base";
import { CTransparentButton } from "../../componets/button/transparent";
import { CustomCursorContext } from "../../contexts/cursor";
import { useWindowSize } from "../../contexts/screen-size";
import { scrollObservers } from "../../utils/scroll-observers";
import {
  Connector_Contact_Link,
  Connector_Docs_Link,
} from "../../constants";
import corepassWog from "../../assets/icons/corepass-wog-png.png";
import briefcaseIcon from "../../assets/icons/briefcase-new-wob.png";
import trademarkIcon from "../../assets/icons/trademark-3.svg";
import {
  CapabilitiesSection,
  ConnectorStyled,
  FaqSection,
  HeroSection,
  PasswordlessSection,
  QuickstartSection,
} from "./connector-styled";
import {
  CONNECTOR_CAPABILITIES,
  CONNECTOR_FAQ,
  CONNECTOR_STEPS,
} from "./data";
import { ConnectorAccordion } from "./components/connector-accordion";

const REVEALED_SECTIONS = [
  "connectorHero",
  "connectorPasswordless",
  "connectorCapabilities",
  "connectorQuickstart",
  "connectorFaq",
];

const BULLET_CLASS = {
  login: "bullet-login",
  kyc: "bullet-kyc",
  kyb: "bullet-kyb",
};

export const Connector = () => {
  const { pathname } = useLocation();
  const { setType } = useContext(CustomCursorContext);
  const { windowHeight } = useWindowSize();

  const [containerVisibility, setContainerVisibility] = useState({
    connectorHero: false,
    connectorPasswordless: false,
    connectorCapabilities: false,
    connectorQuickstart: false,
    connectorFaq: false,
  });
  const containerVisibilityRef = useRef(containerVisibility);

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [pathname]);

  useEffect(() => {
    const { observer10 } = scrollObservers(
      containerVisibilityRef,
      setContainerVisibility
    );
    REVEALED_SECTIONS.forEach((id) => {
      const element = document.getElementById(id);
      if (element) observer10.observe(element);
    });
    return () => {
      observer10.disconnect();
    };
  }, []);

  const hoverCursor = {
    onMouseEnter: () => {
      setType("hover");
    },
    onMouseLeave: () => {
      setType("");
    },
  };

  return (
    <ConnectorStyled>
      <HeroSection id="connectorHero" containerVisibility={containerVisibility}>
        <img
          src={trademarkIcon}
          alt=""
          aria-hidden="true"
          className="hero-trademark"
        />
        <CTypography
          color="var(--Blue)"
          weight="bold"
          className="connector-badge-text"
        >
          CorePass Connector
        </CTypography>

        <h1 className="hero-title">
          One integration,{" "}
          <span className="gradient-font">every verified identity.</span>
        </h1>

        <CTypography
          color="var(--Grey)"
          weight="medium"
          size="largest"
          className="hero-body"
        >
          CorePass Connector is an authentication and authorization gateway that
          lets your platform sign people in, ask them for the identity data you
          are obliged to collect, and receive it directly from the person who
          owns it, without ever holding a password. Built on OAuth 2.0 and
          delivered over a decentralized, peer-to-peer exchange, it replaces
          registration forms, password resets and manual document checks with a
          single verified handover your users approve on their own phone.
        </CTypography>

        <div className="button-container">
          <div className="magnet-container">
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={Connector_Contact_Link}
            >
              <CBaseButton
                className="contained-button magnet-item"
                {...hoverCursor}
              >
                <CTypography
                  color="var(--White)"
                  weight="bold"
                  size="larger"
                  className="button-label"
                >
                  Get in touch
                </CTypography>
              </CBaseButton>
            </a>
          </div>
          <div className="magnet-container">
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={Connector_Docs_Link}
            >
              <CTransparentButton
                className="WBorder-button magnet-item"
                {...hoverCursor}
              >
                <CTypography
                  color="var(--Black)"
                  weight="bold"
                  size="larger"
                  className="button-label"
                >
                  Read the docs
                </CTypography>
              </CTransparentButton>
            </a>
          </div>
        </div>
      </HeroSection>

      <PasswordlessSection
        id="connectorPasswordless"
        containerVisibility={containerVisibility}
      >
        <div className="centerContent">
          <img
            src={corepassWog}
            alt=""
            aria-hidden="true"
            className="logoTop floating"
          />
          <CTypography
            color="var(--Blue)"
            weight="bold"
            className="connector-badge-text"
          >
            Passwordless by default
          </CTypography>

          <h2 className="band-title">
            Sign people in{" "}
            <span className="gradient-font">
              without a single stored credential.
            </span>
          </h2>

          <CTypography
            color="var(--Grey)"
            weight="medium"
            className="band-body"
          >
            Your login page shows a QR code on desktop and a single link button
            on mobile; the person approves the request inside CorePass, and your
            application receives a signed token carrying their Core ID, the
            address that identifies them across the Core ecosystem. There are no
            passwords, email addresses or phone numbers to collect, verify,
            reset or store, so an entire category of account-recovery work never
            reaches your support queue at all.
          </CTypography>

          <img
            src={briefcaseIcon}
            alt=""
            aria-hidden="true"
            className="logoBottom floating-bottom"
          />
        </div>
      </PasswordlessSection>

      <CapabilitiesSection
        id="connectorCapabilities"
        containerVisibility={containerVisibility}
        screenHeight={windowHeight}
      >
        <div className="left-column">
          <CTypography
            color="var(--Blue)"
            weight="bold"
            className="connector-badge-text"
          >
            What it does
          </CTypography>

          <h2 className="section-title">
            Three things your platform{" "}
            <span className="gradient-font">stops having to build.</span>
          </h2>

          <CTypography
            color="var(--Grey)"
            weight="medium"
            size="largest"
            className="section-body"
          >
            Authentication, personal data collection and business verification
            are three separate projects in most roadmaps, each with its own
            storage, its own audit trail and its own recovery flow. The
            Connector answers all three over one integration, and leaves the
            data itself with the person or the company it describes.
          </CTypography>

          <div className="magnet-container">
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={Connector_Contact_Link}
            >
              <CBaseButton
                className="contained-button magnet-item"
                {...hoverCursor}
              >
                <CTypography
                  color="var(--White)"
                  weight="bold"
                  size="larger"
                  className="button-label"
                >
                  Get in touch
                </CTypography>
              </CBaseButton>
            </a>
          </div>
        </div>

        <div className="right-column">
          {CONNECTOR_CAPABILITIES.map(({ id, title, body, bullets }) => (
            <div className="card" key={id}>
              <h3 className="card-title">{title}</h3>
              <CTypography
                color="var(--Grey)"
                weight="medium"
                size="larger"
                className="card-body"
              >
                {body}
              </CTypography>
              <ul className={BULLET_CLASS[id]}>
                {bullets.map((bullet) => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </CapabilitiesSection>

      <QuickstartSection
        id="connectorQuickstart"
        containerVisibility={containerVisibility}
      >
        <div className="top-frame">
          <CTypography
            color="var(--Blue)"
            weight="bold"
            className="connector-badge-text"
          >
            Up and running
          </CTypography>

          <h2 className="section-title">
            Login working{" "}
            <span className="gradient-font">in under five minutes.</span>
          </h2>

          <CTypography
            color="var(--Grey)"
            weight="medium"
            size="largest"
            className="section-body"
          >
            The login flow is one library, one page you already own and one code
            exchange, so a developer with a registered client can have a working
            sign-in before the rest of the integration has even been scoped.
            Everything deeper — data requests, callbacks, signature verification
            and pricing — is documented field by field and waiting for you when
            you need it.
          </CTypography>
        </div>

        <div className="steps">
          {CONNECTOR_STEPS.map(({ step, title, body }) => (
            <div className="step" key={step}>
              <CTypography
                color="var(--Blue)"
                weight="bold"
                className="step-index"
              >
                {step}
              </CTypography>
              <h3 className="step-title">{title}</h3>
              <CTypography
                color="var(--Grey)"
                weight="medium"
                size="larger"
                className="step-body"
              >
                {body}
              </CTypography>
            </div>
          ))}
        </div>
      </QuickstartSection>

      <FaqSection
        id="connectorFaq"
        containerVisibility={containerVisibility}
        screenHeight={windowHeight}
      >
        <div className="left-column">
          <CTypography
            color="var(--Blue)"
            weight="bold"
            className="connector-badge-text"
          >
            Questions from engineering
          </CTypography>

          <h2 className="section-title">
            Still deciding?{" "}
            <span className="gradient-font">The details are public.</span>
          </h2>

          <CTypography
            color="var(--Grey)"
            weight="medium"
            size="largest"
            className="section-body"
          >
            The Connector documentation covers every field name, status
            transition, callback signature and price tier in full, and the
            answers below are the ones that most often decide whether an
            integration is a week of work or an afternoon.
          </CTypography>

          <div className="magnet-container">
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={Connector_Docs_Link}
            >
              <CTransparentButton
                className="WBorder-button magnet-item"
                {...hoverCursor}
              >
                <CTypography
                  color="var(--Black)"
                  weight="bold"
                  size="larger"
                  className="button-label"
                >
                  Read the docs
                </CTypography>
              </CTransparentButton>
            </a>
          </div>
        </div>

        <div className="right-column">
          <ConnectorAccordion accordionOptions={CONNECTOR_FAQ} />
        </div>
      </FaqSection>
    </ConnectorStyled>
  );
};
